import { useCallback, useEffect, useRef, useState } from 'react'
import type { AnimationEvent, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'baseui'
import { lightTheme } from '@/customTheme'
import { MOBILE_MEDIA_QUERY } from '@/constants/responsive'
import DefaultButton from '@/components/DefaultButton'
import { Z_INDEX } from '@/constants/z-index'
import useModal from '@/hooks/useModal'
import { ShowModalType } from '@/scenes/engine/common/constants'
import LimitedOfferArtwork from './LimitedOfferArtwork'
import LimitedOfferCountdown from './LimitedOfferCountdown'
import { useLimitedOfferPrices } from './useLimitedOfferPrices'
import { installLimitedOfferDebugHook, openLimitedOfferPaywall } from './limitedOffer'
import {
  LIMITED_OFFER_DISCOUNT_PERCENT,
  LIMITED_OFFER_EXIT_GUARD_MS,
} from './limitedOfferConfig'
import {
  markLimitedOfferShown,
  settleLimitedOffer,
  startLimitedOfferSession,
  type LimitedOfferDismissMethod,
} from './limitedOfferTrigger'

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'
const TITLE_ID = 'limited-offer-title'
const TITLE_FONT = lightTheme.typography.Title14Bold.fontFamily
const BODY_FONT = lightTheme.typography.Small14Semibold.fontFamily

/** `hidden` renders nothing; `closing` plays the exit before unmounting. */
type Phase = 'hidden' | 'open' | 'closing'

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Keeps Tab and Shift+Tab cycling through the card's own controls. */
function trapTab(event: KeyboardEvent, card: HTMLElement) {
  const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE))
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  const outside = !card.contains(active) || active === card
  if (!first) {
    event.preventDefault()
  } else if (event.shiftKey && (outside || active === first)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && (outside || active === last)) {
    event.preventDefault()
    first.focus()
  }
}

const Scrim = styled<{ $closing: boolean }, 'div'>('div', ({ $closing }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: Z_INDEX.MODAL,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.5)',
  animationName: ($closing
    ? { from: { opacity: 1 }, to: { opacity: 0 } }
    : { from: { opacity: 0 }, to: { opacity: 1 } }) as any,
  animationDuration: $closing ? '180ms' : '240ms',
  animationTimingFunction: $closing ? 'ease-in' : 'ease-out',
  animationFillMode: 'both',
  [REDUCED_MOTION]: { animationName: 'none' },
}))

const Card = styled<{ $closing: boolean }, 'div'>('div', ({ $closing }) => ({
  position: 'relative',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  width: '400px',
  height: 'auto',
  maxHeight: 'calc(100vh - 48px)',
  overflow: 'hidden',
  [MOBILE_MEDIA_QUERY]: {
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100dvh - 24px)',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  },
  borderRadius: '24px',
  background: lightTheme.colors.white,
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.28)',
  animationName: ($closing
    ? {
        from: { opacity: 1, transform: 'none' },
        to: { opacity: 0, transform: 'translateY(6px) scale(0.985)' },
      }
    : {
        from: { opacity: 0, transform: 'translateY(12px) scale(0.96)' },
        to: { opacity: 1, transform: 'none' },
      }) as any,
  animationDuration: $closing ? '180ms' : '340ms',
  animationTimingFunction: $closing ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)',
  animationFillMode: 'both',
  // Focused on open so screen readers start at the dialog; it isn't a control.
  ':focus': { outline: 'none' },
  [REDUCED_MOTION]: { animationName: 'none' },
}))

const Body = styled('div', {
  flex: '1 0 auto',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  padding: '28px 24px 24px',
})

const Title = styled('h2', {
  margin: 0,
  fontFamily: TITLE_FONT,
  fontSize: '26px',
  fontWeight: 800,
  lineHeight: '30px',
  letterSpacing: '-0.39px',
  color: lightTheme.colors.blackGray,
  textAlign: 'center',
  textWrap: 'pretty',
} as any)

const Prices = styled<{ $loading: boolean }, 'div'>('div', ({ $loading }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  visibility: $loading ? 'hidden' : 'visible',
}))

const PriceRow = styled('div', {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'center',
  gap: '10px',
})

const ListPrice = styled('span', {
  fontFamily: BODY_FONT,
  fontSize: '18px',
  fontWeight: 500,
  color: lightTheme.colors.grayScale400,
  textDecoration: 'line-through',
})

const OfferPrice = styled('span', {
  fontFamily: TITLE_FONT,
  fontSize: '44px',
  fontWeight: 800,
  lineHeight: '44px',
  letterSpacing: '-0.88px',
  color: lightTheme.colors.pink,
})

const PriceUnit = styled('span', {
  fontFamily: BODY_FONT,
  fontSize: '16px',
  fontWeight: 500,
  color: lightTheme.colors.grayScale400,
})

const BillingNote = styled('p', {
  margin: 0,
  fontFamily: BODY_FONT,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: lightTheme.colors.grayScale600,
})

const Actions = styled('div', {
  marginTop: '4px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '14px',
})

// DefaultButton's text variant doesn't match this smaller grey style.
const DeclineButton = styled('button', {
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: BODY_FONT,
  fontSize: '13px',
  fontWeight: 500,
  color: lightTheme.colors.grayScale400,
  ':hover': { color: lightTheme.colors.grayScale600 },
  // Same keyboard ring as DefaultButton.
  ':focus-visible': { outline: `2px solid ${lightTheme.colors.primary}`, outlineOffset: '2px' },
})

/**
 * Hands focus back to what had it before the offer opened. Skipped when
 * something else (another modal) has already taken focus.
 */
function restoreFocus(card: HTMLElement | null, target: HTMLElement | null) {
  const active = document.activeElement
  const focusIsOurs = !active || active === document.body || !!card?.contains(active)
  if (target?.isConnected && focusIsOurs) target.focus({ preventScroll: true })
}

/**
 * Limited-offer promo. The modal store requests it; this component owns the
 * open/closing phase so the exit animation can finish before unmounting.
 */
function ModalLimitedOffer() {
  const { t } = useTranslation()
  const { showModalType, setShowModalType } = useModal()
  const isRequested = showModalType === ShowModalType.LIMITED_OFFER
  const [phase, setPhase] = useState<Phase>('hidden')
  // Bumped on every open: the remount replays the entrance and resets the countdown.
  const [openCount, setOpenCount] = useState(0)

  const wasRequested = useRef(false)
  const cardRef = useRef<HTMLDivElement>(null)
  // What had focus before the offer opened, so closing can hand it back.
  const returnFocus = useRef<HTMLElement | null>(null)
  const prices = useLimitedOfferPrices(isRequested)

  useEffect(() => installLimitedOfferDebugHook(), [])
  useEffect(() => startLimitedOfferSession(), [])

  useEffect(() => {
    if (isRequested) {
      const active = document.activeElement
      // Re-opened mid-exit: keep the original target rather than the old card.
      if (active instanceof HTMLElement && !cardRef.current?.contains(active)) returnFocus.current = active
      wasRequested.current = true
      setPhase('open')
      setOpenCount(count => count + 1)
      markLimitedOfferShown()
      return
    }
    if (wasRequested.current) {
      // Replaced by another modal without a user choice.
      settleLimitedOffer(null)
      // Cleared first so the focus guard, still mounted until the exit, lets this through.
      wasRequested.current = false
      restoreFocus(cardRef.current, returnFocus.current)
    }
    returnFocus.current = null
    setPhase(current => (current === 'open' ? 'closing' : current))
  }, [isRequested])

  const close = useCallback(
    (outcome: 'accepted' | LimitedOfferDismissMethod) => {
      if (phase !== 'open') return
      // The paywall opens on top, so focus must not go back to the page behind it.
      if (outcome === 'accepted') returnFocus.current = null
      settleLimitedOffer(outcome)
      setShowModalType(null)
      if (outcome === 'accepted') openLimitedOfferPaywall()
    },
    [phase, setShowModalType],
  )

  // Settle on the scrim's own animationend; the guard covers cases where it never fires.
  useEffect(() => {
    if (phase !== 'closing') return
    const guardId = window.setTimeout(() => setPhase('hidden'), LIMITED_OFFER_EXIT_GUARD_MS)
    return () => window.clearTimeout(guardId)
  }, [phase])

  useEffect(() => {
    if (phase === 'open') cardRef.current?.focus({ preventScroll: true })
  }, [phase, openCount])

  useEffect(() => {
    if (phase !== 'open') return
    // Focus moved behind the modal some other way (e.g. a screen reader): bring it back.
    // A modal that replaced this one keeps its focus, even before our close effect runs.
    const onFocusIn = (event: FocusEvent) => {
      const card = cardRef.current
      const target = event.target
      if (!wasRequested.current || !card || !(target instanceof Element) || card.contains(target)) return
      if (target.closest('[aria-modal="true"]')) return
      card.focus({ preventScroll: true })
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [phase])

  useEffect(() => {
    if (phase !== 'open') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && cardRef.current) {
        // Like Escape below: the modal owns Tab, so page handlers behind it don't see it.
        event.stopPropagation()
        trapTab(event, cardRef.current)
        return
      }
      if (event.key !== 'Escape') return
      // The modal is on top, so it owns Escape: handle it first (window capture)
      // and stop it, so page handlers behind it (Batch selection, the AI prompt)
      // neither swallow it nor act on it.
      event.preventDefault()
      event.stopPropagation()
      close('Escape')
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [phase, close])

  // Listen on the scrim itself so a drag from the card onto the scrim doesn't dismiss.
  const onScrimMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    // The default mousedown focus change runs after the close and would undo the focus return.
    event.preventDefault()
    close('Scrim')
  }

  const onScrimAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (phase === 'closing' && event.target === event.currentTarget) setPhase('hidden')
  }

  if (phase === 'hidden') return null
  const closing = phase === 'closing'

  return (
    <Scrim
      key={openCount}
      $closing={closing}
      onMouseDown={onScrimMouseDown}
      onAnimationEnd={onScrimAnimationEnd}
    >
      <Card
        ref={cardRef}
        $closing={closing}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
      >
        <LimitedOfferArtwork onClose={() => close('Close')} />
        <Body>
          <Title id={TITLE_ID}>{t('We have a special discount just for you')}</Title>
          {/* Keeps its space while prices load, so nothing moves when they appear. */}
          <Prices $loading={!prices}>
            <PriceRow>
              {prices?.listPerMonth && <ListPrice>{prices.listPerMonth}</ListPrice>}
              <OfferPrice>{prices?.offerPerMonth ?? '\u00a0'}</OfferPrice>
              <PriceUnit>/{t('month')}</PriceUnit>
            </PriceRow>
            <BillingNote>{t('billed yearly %@', { arg: prices?.billedYearly ?? '' })}</BillingNote>
          </Prices>
          <LimitedOfferCountdown />
          <Actions>
            <DefaultButton
              label={t('Get %@% off', { arg: LIMITED_OFFER_DISCOUNT_PERCENT })}
              size="large"
              type="primary"
              fullWidth
              onClick={() => close('accepted')}
            />
            <DeclineButton type="button" onClick={() => close('No Thanks')}>
              {t('No thanks')}
            </DeclineButton>
          </Actions>
        </Body>
      </Card>
    </Scrim>
  )
}

export default ModalLimitedOffer
